import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Upload, Trash2, Eye, EyeOff, Loader2, Image as ImageIcon } from 'lucide-react';

const AdvertisementsManagementPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const [newAd, setNewAd] = useState({
    title: '',
    description: '',
    cta: '',
    link: '',
    color: 'from-blue-600 to-blue-800',
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchAds();
  }, [isAdmin, navigate]);

  const fetchAds = async () => {
    try {
      const response = await api.get('/admin/advertisements');
      setAds(response.data.ads || []);
    } catch (error) {
      toast.error('Erreur de chargement des publicités');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewAd({ ...newAd, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateAd = async (e) => {
    e.preventDefault();
    
    if (!newAd.title || !newAd.description) {
      toast.error('Titre et description obligatoires');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', newAd.title);
      formData.append('description', newAd.description);
      formData.append('cta', newAd.cta);
      formData.append('link', newAd.link);
      formData.append('color', newAd.color);
      if (newAd.image) {
        formData.append('image', newAd.image);
      }

      await api.post('/admin/advertisements', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('✅ Publicité créée avec succès !');
      setNewAd({
        title: '',
        description: '',
        cta: '',
        link: '',
        color: 'from-blue-600 to-blue-800',
        image: null
      });
      setImagePreview(null);
      fetchAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de création');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleActive = async (adId) => {
    try {
      await api.patch(`/admin/advertisements/${adId}/toggle`);
      toast.success('Statut mis à jour');
      fetchAds();
    } catch (error) {
      toast.error('Erreur de mise à jour');
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Supprimer cette publicité ?')) return;
    
    try {
      await api.delete(`/admin/advertisements/${adId}`);
      toast.success('Publicité supprimée');
      fetchAds();
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const colorOptions = [
    { value: 'from-blue-600 to-blue-800', label: 'Bleu' },
    { value: 'from-green-600 to-green-800', label: 'Vert' },
    { value: 'from-red-600 to-red-800', label: 'Rouge' },
    { value: 'from-purple-600 to-purple-800', label: 'Violet' },
    { value: 'from-yellow-600 to-yellow-800', label: 'Jaune' },
    { value: 'from-pink-600 to-pink-800', label: 'Rose' },
    { value: 'from-cyan-600 to-cyan-800', label: 'Cyan' },
    { value: 'from-orange-600 to-orange-800', label: 'Orange' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            📢 Gestion des Publicités
          </h1>
          <p className="text-zinc-400">
            Créez et gérez les publicités affichées sur toutes les pages du site
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formulaire de création */}
          <Card className="glass border-zinc-800">
            <CardHeader>
              <CardTitle>Créer une Nouvelle Publicité</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateAd} className="space-y-4">
                <div>
                  <Label>Titre *</Label>
                  <Input
                    value={newAd.title}
                    onChange={(e) => setNewAd({ ...newAd, title: e.target.value })}
                    placeholder="Ex: 🎬 Nouveau service disponible !"
                    className="bg-zinc-900 border-zinc-800 text-white"
                    required
                  />
                </div>

                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={newAd.description}
                    onChange={(e) => setNewAd({ ...newAd, description: e.target.value })}
                    placeholder="Description de la publicité"
                    className="bg-zinc-900 border-zinc-800 text-white"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <Label>Texte du Bouton</Label>
                  <Input
                    value={newAd.cta}
                    onChange={(e) => setNewAd({ ...newAd, cta: e.target.value })}
                    placeholder="Ex: Réserver, Découvrir, etc."
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>

                <div>
                  <Label>Lien</Label>
                  <Input
                    value={newAd.link}
                    onChange={(e) => setNewAd({ ...newAd, link: e.target.value })}
                    placeholder="/service/..."
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>

                <div>
                  <Label>Couleur de Fond (si pas d'image)</Label>
                  <select
                    value={newAd.color}
                    onChange={(e) => setNewAd({ ...newAd, color: e.target.value })}
                    className="w-full p-3 bg-zinc-900 border border-zinc-800 rounded text-white"
                  >
                    {colorOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Image d'Artiste / Photo Promotionnelle (optionnel)</Label>
                  <div className="mt-2">
                    <label className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-zinc-900">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="max-h-40 rounded-lg mb-2"
                        />
                      ) : (
                        <>
                          <ImageIcon className="text-zinc-500 mb-2" size={32} />
                          <p className="text-sm text-zinc-400">
                            Cliquez pour uploader une image
                          </p>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-[#E50914] hover:bg-[#F8212C]"
                >
                  {uploading ? <Loader2 className="animate-spin" /> : 'Créer la Publicité'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Liste des publicités */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Publicités Existantes ({ads.length})</h2>
            
            {ads.length === 0 ? (
              <Card className="glass border-zinc-800">
                <CardContent className="py-12 text-center text-zinc-400">
                  Aucune publicité créée
                </CardContent>
              </Card>
            ) : (
              ads.map((ad) => (
                <Card key={ad.id} className="glass border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {ad.image ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${ad.image}`}
                          alt={ad.title}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      ) : (
                        <div className={`w-24 h-24 rounded-lg bg-gradient-to-r ${ad.color}`}></div>
                      )}
                      
                      <div className="flex-1">
                        <h3 className="text-white font-bold">{ad.title}</h3>
                        <p className="text-sm text-zinc-400 mb-2">{ad.description}</p>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${ad.active ? 'bg-green-900 text-green-300' : 'bg-zinc-800 text-zinc-400'}`}>
                            {ad.active ? 'Active' : 'Désactivée'}
                          </span>
                          {ad.link && <span className="text-xs text-zinc-500">{ad.link}</span>}
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleActive(ad.id)}
                          className="text-xs"
                        >
                          {ad.active ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(ad.id)}
                          className="text-red-500 text-xs"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdvertisementsManagementPage;
